/**
 * Hook paginado server-side para sessões de resultados do fluxo
 * Utiliza a RPC get_flow_results_paginated com paginação offset-based
 * Segue padrão do useServiceQueuePaginated
 * 
 * @release v1.0.0 - 2026-03-08
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger condicional para debug
const logger = createLogger('FlowResultsPaginated');

/** Filtros para a listagem de sessões */
export interface FlowSessionFilters {
  startDate?: string | null;
  endDate?: string | null;
  inboxId?: string | null;
  triggerType?: string | null;
  status?: string | null;
  search?: string | null;
  source?: 'all' | 'link' | 'whatsapp' | null;
  /** ID do node para drill-down do funil (filtra sessões que passaram por este node) */
  nodeId?: string | null;
}

/** Item de sessão retornado pela RPC paginada */
export interface PaginatedFlowSession {
  id: string;
  source: string;
  trigger_type: string;
  trigger_detail: string | null;
  contact_name: string | null;
  phone: string | null;
  status: string;
  started_at: string;
  completed_at: string | null;
  current_node_id: string | null;
  context: Record<string, unknown> | null;
  inbox_name: string | null;
  step_responses: Record<string, string>;
  total_count: number;
  /** ID da conversa associada (apenas sessões WhatsApp) */
  conversation_id: string | null;
}

/** Parâmetros de paginação e ordenação */
interface PaginationParams {
  page: number;
  pageSize: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}

/**
 * Hook para buscar sessões do fluxo com paginação server-side
 * 
 * @param flowId - ID do fluxo
 * @param filters - Filtros de período, inbox, trigger, status, busca, origem
 * @param pagination - Parâmetros de paginação e ordenação
 */
export function useFlowResultsPaginated(
  flowId: string | undefined,
  filters: FlowSessionFilters,
  pagination: PaginationParams
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['flow-results-paginated', flowId, currentTenant?.id, filters, pagination],
    queryFn: async () => {
      logger.debug('Buscando sessões paginadas do fluxo:', { flowId, filters, pagination });

      // Converte 'all' para null no filtro de source
      const sourceFilter = filters.source === 'all' ? null : filters.source;

      const { data, error } = await (supabase.rpc as any)(
        'get_flow_results_paginated',
        {
          p_flow_id: flowId,
          p_page: pagination.page,
          p_page_size: pagination.pageSize,
          p_sort_field: pagination.sortField || 'started_at',
          p_sort_direction: pagination.sortDirection || 'desc',
          p_start_date: filters.startDate || null,
          p_end_date: filters.endDate || null,
          p_inbox_id: filters.inboxId || null,
          p_trigger_type: filters.triggerType || null,
          p_status: filters.status || null,
          p_search: filters.search || null,
          p_source: sourceFilter || null,
          p_node_id: filters.nodeId || null,
          p_tenant_hint: currentTenant?.id || null,
        }
      );

      if (error) {
        logger.error('Erro ao buscar sessões paginadas do fluxo:', error);
        throw error;
      }

      // Total count vem em cada row (pattern COUNT(*) OVER())
      const rawItems = (data || []) as PaginatedFlowSession[];
      const totalCount = rawItems[0]?.total_count ?? 0;

      logger.debug(`Recebidos ${rawItems.length} sessões, total: ${totalCount}`);

      return {
        items: rawItems,
        totalCount: Number(totalCount),
        totalPages: Math.ceil(Number(totalCount) / pagination.pageSize) || 1,
      };
    },
    enabled: !!flowId && !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    items: query.data?.items || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => {
      queryClient.invalidateQueries({
        queryKey: ['flow-results-paginated', flowId, currentTenant?.id],
      });
    },
  };
}
